// Editor-side metadata for PMM v4 properties. Pure data + small SDK
// of pure functions. No React, no Flux, no Redux.
//
// Lineage:
//   - section taxonomy: originally PMM v3 player/2.7.js 1:1; regrouped and
//     relabelled once the v4 migration no longer needed to be invisible
//     (see the comment on `sections`). Property KEYS are untouched by that
//     -- sections are a nav concern and are never persisted.
//   - display labels + descriptions for 28 retained keys: PMM v3 2.7
//   - 3 new beta.5 keys (showDateTime, showTitleOverlay,
//     skipButtonDuration): hand-written
//   - 19 theme keys (9 in 4.1.0-beta.2, 5 structural in beta.3, the
//     modal surface in beta.5, 4 active-tab pill keys in beta.6):
//     hand-written. The `theme` section is
//     net-new -- it has no v3 2.7 ancestor, because pre-Shadow-DOM
//     theming happened via host-page CSS reach-in, not via prefs.
//
// Parity with PMM: enforced via
// tests/playerPreferences/metaMap-parity.test.js
// (Object.keys(metaMap).sort() === [...propertyKeys].sort()).

/**
 * @typedef {'boolean' | 'text' | 'email' | 'number' | 'select' | 'legal' | 'assets' | 'color' | 'font' | 'length'} InputType
 *
 * @typedef {object} MetaOption
 * @property {string | number | boolean} value
 * @property {string} label
 *
 * @typedef {object} MetaEntry
 * @property {string} section            section key; must match a sections[].key
 * @property {number} order              sort within section
 * @property {string} displayName
 * @property {string} description        required; matches v2.7 UI precedent
 * @property {string} [hint]            short one-line label for compact UIs (Theme tab);
 *                                     `description` stays canonical for docs + tooltips
 * @property {InputType} inputType
 * @property {MetaOption[]} [options]    for select
 * @property {number} [min]
 * @property {number} [max]
 * @property {number} [step]
 * @property {string} [capability]       requires this capability flag to render
 * @property {boolean} [hidden]          temporarily hide the input (unreleased feature); property remains valid on the wire
 */

// Section list. NO LONGER v3 2.7 sortOrder -- the v4 migration deliberately
// preserved v3's grouping so the change was invisible to admins, and that
// debt is now paid off. What v3 left behind:
//
//   - "General" was a dumping ground holding two unrelated things: viewer
//     actions (download, add-to-calendar) and boilerplate (legal, contact).
//     Both now have a real home and the section is gone.
//   - "Player Configuration" named the whole editor, not its contents, and
//     held three unrelated clusters. Playback controls kept the tab (as
//     `playback`), the slates moved to `interstitials`.
//   - "Social & Sharing" was tautological, and had share/embed separated
//     from download/calendar for no reason a user could see. All five are
//     "what a viewer can do", so they are one section now.
//   - The "Event " prefix on Documents / Transcription / Agenda
//     distinguished nothing -- everything here is event data -- and cost
//     horizontal room in the nav.
//
// Section keys are internal (nav + tab lookup only, never persisted), so
// surviving sections keep theirs to limit churn even where the label moved.
export const sections = [
  // First, and so also the tab the editor opens on -- PlayerPreferenceEditor
  // seeds activeTabKey from sections[0]. Layout leads because it decides the
  // shape everything else is arranged inside; theming a player you have not
  // yet laid out is the wrong order to work in.
  {
    key: 'layout',
    displayName: 'Layout',
    description: 'Choose how the player and its surrounding elements are arranged on the page.',
  },
  // Second, so it sits directly under Layout -- the two are the whole of how
  // the player LOOKS, and the rest of the list is what it SHOWS. Its metaMap
  // block stays at the BOTTOM of the file: those 19 entries are ordered by
  // the PMM beta that introduced them, which is worth more as a reading
  // order than matching the nav.
  {
    key: 'theme',
    displayName: 'Theme Builder',
    description: 'Match the player to your site\'s branding. Every setting left blank falls back to the player\'s own default, so an unthemed preference looks exactly as it does today.',
  },
  {
    key: 'eventMetadata',
    displayName: 'Event Details',
    description: 'Which event details (date, categories, description, location) appear alongside the player.',
  },
  {
    key: 'playback',
    displayName: 'Playback',
    description: 'How the video plays and how it reaches the viewer: speed controls, skip interval, and peer-to-peer delivery.',
  },
  {
    key: 'interstitials',
    displayName: 'Offline & Break Media',
    description: 'What the player shows when the event has no media of its own before it goes live, and while it is on break.',
  },
  {
    key: 'viewerActions',
    displayName: 'Viewer Actions',
    description: 'What a viewer can do with the event: share it, embed it, download it, add it to a calendar.',
  },
  {
    key: 'documents',
    displayName: 'Documents',
    description: 'Display PDFs and other files attached to the event.',
  },
  {
    key: 'transcription',
    displayName: 'Transcript',
    description: 'Display generated transcripts and related navigation aids. Requires transcripts produced through our system.',
  },
  {
    key: 'agenda',
    displayName: 'Agenda',
    description: 'Display the event\'s agenda timeline and current item indicator.',
  },
  {
    key: 'legalContact',
    displayName: 'Legal & Contact',
    description: 'The download legal notice, and where viewers report a problem with an event\'s media.',
  },
];

/** @type {Record<string, MetaEntry>} */
export const metaMap = {

  // ----- Layout -----

  basic: {
    section: 'layout',
    order: 1,
    displayName: 'Basic Layout',
    description: 'If turned on this will force the player to only show the video player, omitting all other preferences.',
    inputType: 'boolean',
  },

  showTitleOverlay: {
    section: 'layout',
    order: 2,
    displayName: 'Title Overlay',
    description: 'Render the event title as a YouTube-style overlay on the player. Most useful in Basic Layout where other event chrome is hidden.',
    inputType: 'boolean',
    hidden: true,
  },

  // ----- Event Details -----

  dateFormat: {
    section: 'eventMetadata',
    order: 1,
    displayName: 'Event Date Format',
    description: 'Specify a format for the Event\'s Start Date/Time.',
    inputType: 'select',
    // SelectInput renders these via moment().format(opt) when
    // preferenceName === 'dateFormat', so labels update to today's
    // date. Keep as a flat string[] -- the legacy contract that
    // SelectInput (shared with listing) expects.
    options: [
      'dddd, hh:mm a',
      'MMM. D, h:mm a',
      'MMMM D, h:mm a',
      'MM/DD/YYYY, h:mm a',
    ],
  },

  showDateTime: {
    section: 'eventMetadata',
    order: 2,
    displayName: 'Show Date and Time',
    description: 'Show the event\'s scheduled start date and time on the player.',
    inputType: 'boolean',
  },

  categories: {
    section: 'eventMetadata',
    order: 3,
    displayName: 'Show Assigned Categories',
    description: 'Show the Categories assigned to the Event being viewed if any.',
    inputType: 'boolean',
  },

  description: {
    section: 'eventMetadata',
    order: 4,
    displayName: 'Display Event Description',
    description: 'Show the Description of the Event if set.',
    inputType: 'boolean',
  },

  location: {
    section: 'eventMetadata',
    order: 5,
    displayName: 'Display Event Location',
    description: 'Show the Location assigned to the Event if set.',
    inputType: 'boolean',
  },
  dateAboveTitle: {
    section: 'eventMetadata',
    order: 6,
    displayName: 'Show Date Above Title',
    description: 'Place the event date and time above the title instead of below it.',
    inputType: 'boolean',
  },

  categoryAlign: {
    section: 'eventMetadata',
    order: 7,
    displayName: 'Category Label Alignment',
    description: 'Whether the category label sits on the left or the right beneath the event description.',
    inputType: 'select',
    options: ['left', 'right'],
  },


  // ----- Playback -----

  playbackSpeed: {
    section: 'playback',
    order: 1,
    displayName: 'Playback Speed Controls',
    description: 'Provide a control in the video player that allows end-users to speed up or slow down media playback.',
    inputType: 'boolean',
  },

  skipButtonDuration: {
    section: 'playback',
    order: 2,
    displayName: 'Skip Button Duration',
    description: 'Number of seconds the player\'s skip-forward and skip-backward buttons jump.',
    inputType: 'number',
    min: 5,
    max: 60,
  },

  // Ordered last in the section: the only one here that is infrastructure
  // rather than something the viewer operates.
  peerToPeer: {
    section: 'playback',
    order: 3,
    displayName: 'Peer-to-Peer (P2P) Content Delivery',
    description: 'Share streaming load between viewers to reduce delivery costs. When enabled, viewers help relay the stream to each other instead of pulling everything from the server. Works in Chrome, Firefox, and Opera; falls back automatically in other browsers.',
    inputType: 'boolean',
    capability: 'isP2pEnabled',
  },

  // ----- Offline & Break Media -----

  offlineSlates: {
    section: 'interstitials',
    order: 1,
    displayName: 'Media interstitials for Offline Events',
    description: 'Assign Global Media Assets to appear in the Video Player when your Event isn\'t Live or Published. Either one image or one or more videos that play in a loop.',
    inputType: 'assets',
  },

  breakSlates: {
    section: 'interstitials',
    order: 2,
    displayName: 'Media interstitials for On-Break Events',
    description: 'Assign Global Media Assets to appear in the Video Player when your Event is On Break. Either one image or one or more videos that play in a loop.',
    inputType: 'assets',
  },

  // ----- Viewer Actions -----

  share: {
    section: 'viewerActions',
    order: 1,
    displayName: 'Sharing',
    description: 'Provide a Sharing tab for embedding and linking.',
    inputType: 'boolean',
  },

  embed: {
    section: 'viewerActions',
    order: 2,
    displayName: 'Provide Code to Re-Embed',
    description: 'Show users an embed code they can paste into their own website to display the video player.',
    inputType: 'boolean',
  },

  hostUrl: {
    section: 'viewerActions',
    order: 3,
    displayName: 'Host URL',
    description: 'Specify your own Host URL to replace our default one in the URLs generated in the Share Tab.',
    inputType: 'text',
  },

  videoDownload: {
    section: 'viewerActions',
    order: 4,
    displayName: 'Provide Video Download Link',
    description: 'Let users download the event\'s video as an MP4 file (when the event has been published).',
    inputType: 'boolean',
  },

  addToCalendar: {
    section: 'viewerActions',
    order: 5,
    displayName: 'Add to Calendar',
    description: 'Adds a button to add the event to a calendar manager, such as Google Calendar. Note that the button will only appear for events scheduled to start more than one hour from when it\'s loaded.',
    inputType: 'boolean',
  },

  // ----- Documents -----

  documentMode: {
    section: 'documents',
    order: 1,
    displayName: 'Document Mode',
    description: 'Show a PDF viewer alongside the media player, displaying the Event\'s document attachments.',
    inputType: 'boolean',
  },

  showDocuments: {
    section: 'documents',
    order: 2,
    displayName: 'Show supplemental Documents',
    description: 'List non-video media (.pdf, .xls, .docx, etc.) assigned to the given event.',
    inputType: 'boolean',
  },

  showAudioAssets: {
    section: 'documents',
    order: 3,
    displayName: 'List Audio Files in Documents Tab',
    description: 'List audio files associated with the event.',
    inputType: 'boolean',
  },

  // ----- Transcript -----

  transcriptVisible: {
    section: 'transcription',
    order: 1,
    displayName: 'Show Transcription',
    description: 'Show the transcript when an event has had one generated on its published media.',
    inputType: 'boolean',
  },

  transcriptKeywords: {
    section: 'transcription',
    order: 2,
    displayName: 'Keyword Navigator',
    description: 'Show a columnar listing of keywords and their frequency found within the transcript.',
    inputType: 'boolean',
  },

  transcriptFollow: {
    section: 'transcription',
    order: 3,
    displayName: 'Follow Transcript',
    description: 'Show the full transcript below the video player and highlight the current portion as the user plays back the media.',
    inputType: 'boolean',
  },

  transcriptNotice: {
    section: 'transcription',
    order: 4,
    displayName: 'Transcript Origination Notice',
    description: 'Transcripts are machine-generated. Provide a dismissible notice that appears above the transcript. Leave empty to hide it entirely.',
    inputType: 'text',
  },

  // ----- Agenda -----

  showAgenda: {
    section: 'agenda',
    order: 1,
    displayName: 'Display Event Agenda',
    description: 'Show the event\'s agenda markers in a tab next to the player.',
    inputType: 'boolean',
  },

  showAgendaLive: {
    section: 'agenda',
    order: 2,
    displayName: 'Display Agenda On Live Events',
    description: 'Show the agenda on the player before the event is published. Does not affect the current-item callout.',
    inputType: 'boolean',
  },

  showCurrentItem: {
    section: 'agenda',
    order: 3,
    displayName: 'Display Current Item',
    description: 'Show the currently marked live agenda index in a callout below the player.',
    inputType: 'boolean',
  },

  // ----- Legal & Contact -----
  //
  // Both were in v3's "General" alongside the download and calendar
  // toggles. They are the only two settings here that are neither a
  // feature toggle nor a display choice -- they are boilerplate a client
  // sets once and rarely revisits, so they sit together and near the end.

  legal: {
    section: 'legalContact',
    order: 1,
    displayName: 'Legal Notice',
    description: 'You can provide a rich-text legal notice for all downloadable audio/video media. Users will be prompted with it before download proceeds.',
    inputType: 'legal',
  },

  contactInfo: {
    section: 'legalContact',
    order: 2,
    displayName: 'Contact Information',
    description: 'In the event that the media for a Live or Published Event isn\'t available (such as corrupt media), you can provide an email so end-users can let you know. Defaults to Invintus\' support email.',
    inputType: 'email',
  },

  // ----- Theme -----
  //
  // The 9 frozen theme tokens (PM sign-off 2026-07-21). Each maps 1:1 to
  // a `--player-*` custom property the player paints onto its shadow host
  // at boot. PMM stores them as opaque strings and defaults every one to
  // '' -- empty means "don't set the property", so the player's shipped
  // CSS default paints and an unthemed preference renders as it does now.
  //
  // Deliberately NOT here: --player-accent-hover, --player-border-hover,
  // and --player-border-light are derived by the player from the base
  // tokens via color-mix, and --player-radius-sm/-lg/-xl keep their CSS
  // defaults (radius is base-only). Giving any of them a control would
  // create a key the player ignores.

  themeAccent: {
    section: 'theme',
    order: 1,
    displayName: 'Accent Color',
    description: 'The player\'s primary brand color: links, focus rings, the active tab indicator, and other interactive highlights. The player derives its own hover shade from this.',
    hint: 'Links, focus rings, active tab',
    inputType: 'color',
  },

  themeTextStrong: {
    section: 'theme',
    order: 2,
    displayName: 'Strong Text Color',
    description: 'The heaviest text on the player: the event title and the active tab label.',
    hint: 'Event title, active tab label',
    inputType: 'color',
  },

  themeText: {
    section: 'theme',
    order: 3,
    displayName: 'Body Text Color',
    description: 'Body copy on the player, including the event description, and the row text in the Documents and Agenda lists. Transcript lines are the exception: they use your muted text color, and the line currently playing uses your strong text color.',
    hint: 'Description and list rows',
    inputType: 'color',
  },

  themeTextMuted: {
    section: 'theme',
    order: 4,
    displayName: 'Muted Text Color',
    description: 'Secondary text: event date and location, the labels on share and download buttons, and every transcript line that is not the one currently playing. The player mutes those on purpose so the current line stands out, which means this color sets the tone of the whole transcript.',
    hint: 'Date, location, buttons, transcript body',
    inputType: 'color',
  },

  themeTextSoft: {
    section: 'theme',
    order: 5,
    displayName: 'Soft Text Color',
    description: 'The faintest text: transcript timecodes and the search field\'s placeholder.',
    hint: 'Timecodes and placeholders',
    inputType: 'color',
  },

  themeTint: {
    section: 'theme',
    order: 6,
    displayName: 'Tint',
    description: 'The ink used for hover and fill states, applied at low opacity. Use black on a light theme and white on a dark one.',
    hint: 'Hover and fill ink',
    inputType: 'color',
  },

  themeBorder: {
    section: 'theme',
    order: 7,
    displayName: 'Border Color',
    description: 'Borders and dividers, including the tab strip underline and button outlines. The player derives its lighter and hover variants from this.',
    hint: 'Dividers and outlines',
    inputType: 'color',
  },

  themeFontFamily: {
    section: 'theme',
    order: 8,
    displayName: 'Font',
    description: 'The typeface used throughout the player. Web fonts must already be loaded by the page hosting the player.',
    hint: 'Typeface for the whole player',
    inputType: 'font',
  },

  themeRadius: {
    section: 'theme',
    order: 9,
    displayName: 'Corner Rounding',
    description: 'How rounded buttons and controls are, from square to fully rounded. Larger surfaces such as dialogs keep the player\'s own rounding.',
    hint: 'Button and control corners',
    inputType: 'length',
    cssProperty: 'border-radius',
    min: 0,
    max: 16,
    step: 1,
  },

  // ----- Theme: structural (4.1.0-beta.3) -----
  //
  // Per-component levers beyond the palette, added so a client with a
  // deliberate look -- filled brand-colored buttons, larger type -- can
  // reproduce it through preferences instead of host-page CSS.

  themeButtonBg: {
    section: 'theme',
    order: 10,
    displayName: 'Button Fill',
    description: 'Background color of the share and download buttons. Leave this blank for the outlined buttons the player ships with; giving it a color makes them filled, and the outline collapses into the fill rather than leaving a stray border.',
    hint: 'Share and Download background',
    inputType: 'color',
  },

  themeButtonText: {
    section: 'theme',
    order: 11,
    displayName: 'Button Text',
    description: 'Color of the label and icon on the share and download buttons. Worth setting alongside a button fill, so the label stays legible against it.',
    hint: 'Share and Download label',
    inputType: 'color',
  },

  themeButtonSize: {
    section: 'theme',
    order: 12,
    displayName: 'Button Size',
    description: 'Text size on the share and download buttons. Their padding and icon scale with it, so the whole button grows rather than just the label.',
    hint: 'Share and Download text size',
    inputType: 'length',
    cssProperty: 'font-size',
    min: 10,
    max: 24,
    step: 1,
  },

  themeBodyFontSize: {
    section: 'theme',
    order: 13,
    displayName: 'Body Text Size',
    // Measured on the beta, not read off the CSS: setting
    // `--player-body-font-size` to 40px resized exactly ten elements, all of
    // them the description block, with 416 transcript rows on screen and not
    // one of them moving. The old wording claimed the transcript too, which is
    // both false and now actively confusing -- transcript text IS sizable, by
    // List Row Text Size.
    description: 'Size of the event description shown below the title. This is the description block only -- the transcript, the tab labels and the list rows each have their own size setting.',
    hint: 'Event description only',
    inputType: 'length',
    cssProperty: 'font-size',
    min: 10,
    max: 24,
    step: 1,
  },

  themeTitleFontSize: {
    section: 'theme',
    order: 14,
    displayName: 'Title Size',
    description: 'Size of the event title shown below the player.',
    hint: 'Event title size',
    inputType: 'length',
    cssProperty: 'font-size',
    min: 12,
    max: 32,
    step: 1,
  },

  // ----- Theme: the rest of the type scale (4.1.0) -----
  //
  // Title, Body and Button covered three surfaces and stopped, leaving the
  // event date, the tab labels and the list rows with no size lever at all --
  // the three Oregon State Legislature asked about by name.
  //
  // These are branding levers, not the accessibility answer. A viewer who
  // needs larger text raises it in their own browser and the player follows,
  // with no preference involved and no client action required (hosted-player
  // #839). That is why none of the three is in Essentials: the case that
  // would have justified promoting one is already handled without a control.

  themeDateFontSize: {
    section: 'theme',
    order: 14.1,
    displayName: 'Event Date Size',
    // Measured on the beta: --player-underlay-date-size moves the date element
    // and nothing else. The location/organisation line beneath it is a bare
    // `text-xs` span with no `part`, so no token reaches it -- it follows the
    // READER's browser font size (text-xs is rem-based, so transform D rewrites
    // it) but not this control. Raising the date well past 12px therefore
    // leaves the location pinned, which is the #839 label-vs-meta mismatch in a
    // new place. The fix is player-side (give that span the date part); until
    // then the control says so rather than surprising an admin.
    description: 'Size of the event date and time shown with the title. The location line beneath it keeps the player\'s own size for now, so a much larger date will not bring it along.',
    hint: 'Date and time only',
    inputType: 'length',
    cssProperty: 'font-size',
    min: 10,
    max: 24,
    step: 1,
  },

  themeTabFontSize: {
    section: 'theme',
    order: 14.2,
    displayName: 'Tab Label Size',
    description: 'Size of the labels on the content tabs (Transcript, Documents, Agenda). The tab icons scale with the label, so the whole tab grows rather than the text alone.',
    hint: 'All tab labels',
    inputType: 'length',
    cssProperty: 'font-size',
    min: 10,
    max: 24,
    step: 1,
  },

  themeListRowFontSize: {
    section: 'theme',
    order: 14.3,
    displayName: 'List Row Text Size',
    description: 'Size of the text in the Documents, Agenda and Transcript lists. One setting drives all three, and each row\'s smaller second line -- a timecode, or a document\'s file details -- scales with it, so a row stays in proportion at any size.',
    hint: 'All three lists',
    inputType: 'length',
    cssProperty: 'font-size',
    // Floors at 12, not the 10 the other size controls use. This is the one
    // size key with a DEPENDENT sub-line: the player pins the row's meta text
    // (the timecode, a document's file details) at 0.7857 of the label, so the
    // slider's floor sets a second, smaller floor the client never chose.
    // Measured on the beta: 14 -> 11, 28 -> 22. At 10 the timecode lands on
    // 7.9px, smaller than anything the player ships. 12 gives 9.4px.
    min: 12,
    max: 24,
    step: 1,
  },

  // ----- Theme: modal surface (4.1.0-beta.5) -----
  //
  // Opt-in themed dialog card. Blank keeps the white card the player ships
  // (where it paints legible dark "island" text); a dark value yields a dark
  // card that carries the themed light text. Pairs with the text colors
  // above -- Control Center owns keeping that background/text pair legible.

  themeModal: {
    section: 'theme',
    order: 15,
    displayName: 'Dialog Background',
    description: 'Background of the pop-up dialogs: Share, Download, and Clip. Blank keeps the player\'s built-in dark dialog text, so it stays readable no matter what your other text colors are. Set a color, even white, and the dialogs switch to using your theme\'s text instead, matching your palette; pair a dark card with the light text colors above, or the dialog text will be unreadable.',
    hint: 'Share/Download/Clip card. Blank keeps the player\'s dark dialog text.',
    inputType: 'color',
  },

  // ----- Theme: active-tab pill (4.1.0-beta.6) -----
  //
  // The content tabs (Transcript, Documents, Agenda) ship as plain labels
  // with an underline marking the active one. These four turn the active tab
  // into a filled pill and let the strip behind it carry a tint -- the shape
  // CVTV and TVW brand by hand today. Blank on all four is the underlined tab
  // exactly as it renders now.

  themeTabActiveBg: {
    section: 'theme',
    order: 16,
    displayName: 'Active Tab Fill',
    description: 'Background color of the selected content tab: Transcript, Documents, or Agenda. Leave this blank for the underlined tab the player ships with; giving it a color turns the tab into a filled pill, and the underline collapses into the fill rather than leaving a stray line beneath it.',
    hint: 'Selected tab background',
    inputType: 'color',
  },

  themeTabActiveIndicator: {
    section: 'theme',
    order: 16.5,
    displayName: 'Active Tab Underline',
    description: 'Color of the line beneath the selected tab. Blank follows the tab fill, so a filled tab shows no line -- which is usually what you want, and is why this setting exists: if you want a filled tab that DOES carry a contrasting line beneath it, this is the only way to say so.',
    hint: 'Line under the selected tab',
    inputType: 'color',
  },

  themeTabActiveText: {
    section: 'theme',
    order: 17,
    displayName: 'Active Tab Label',
    description: 'Color of the selected tab\'s label. Blank uses your strong text color, which is the right choice for an unfilled tab but rarely readable on a filled one; set this alongside a tab fill, and Control Center will offer a label color that reads against it.',
    hint: 'Selected tab text',
    inputType: 'color',
  },

  themeTabActiveRadius: {
    section: 'theme',
    order: 18,
    displayName: 'Active Tab Corners',
    description: 'Corner rounding on the SELECTED tab only. The unselected tabs have their own setting, Tab Corner Rounding, so the two can differ. Only visible once the selected tab has a fill. Around 20px and above the corners meet, giving the fully rounded pill shape.',
    hint: 'Selected tab corners only',
    inputType: 'length',
    cssProperty: 'border-radius',
    min: 0,
    max: 24,
    step: 1,
  },

  themeTabStripBg: {
    section: 'theme',
    order: 19,
    displayName: 'Tab Strip Background',
    description: 'Background color of the strip the content tabs sit in. Blank leaves it transparent, as it is today. The unselected tab labels use your muted text color, so a dark strip needs a muted color light enough to read on it.',
    hint: 'Strip behind the tabs',
    inputType: 'color',
  },

  // ----- Round 2 (PMM 4.1.0-beta.7) -----

  themeListRowBg: {
    section: 'theme',
    order: 20,
    displayName: 'List Row Background',
    description: 'Background color of alternating rows in the Documents, Agenda and Transcript lists. This one setting covers all three lists. Leave blank for the unshaded rows the player ships with.',
    hint: 'Odd rows',
    inputType: 'color',
  },

  themeListRowBgAlt: {
    section: 'theme',
    order: 21,
    displayName: 'List Row Background (alternate)',
    description: 'Background color of every other row, giving the lists a banded look. Set this together with the row background above; leaving it blank means every row shares one color.',
    hint: 'Even rows',
    inputType: 'color',
  },

  themeListRowColor: {
    section: 'theme',
    order: 22,
    displayName: 'List Row Text',
    description: 'Color of the document names, agenda items and transcript lines. Separate from the general body text color, so links in a list can carry your brand without recoloring the event description.',
    hint: 'Row labels',
    inputType: 'color',
  },

  themeListRowDivider: {
    section: 'theme',
    order: 23,
    displayName: 'List Row Divider',
    description: 'Color of the hairline between rows. Set it to match the row background to remove the lines entirely. Separate from the tab strip rule, so one can be shown while the other is hidden.',
    hint: 'Line between rows',
    inputType: 'color',
  },

  themeTranscriptRowBg: {
    section: 'theme',
    order: 24,
    displayName: 'Transcript Row Background',
    description: 'Overrides the list row background for the Transcript only. Leave blank to follow the shared list setting. Use this when the transcript should read differently from the document and agenda lists.',
    hint: 'Transcript only, odd rows',
    inputType: 'color',
  },

  themeTranscriptRowBgAlt: {
    section: 'theme',
    order: 25,
    displayName: 'Transcript Row Background (alternate)',
    description: 'Overrides the alternating row background for the Transcript only. Leave blank to follow the shared list setting.',
    hint: 'Transcript only, even rows',
    inputType: 'color',
  },

  themeTranscriptRowColor: {
    section: 'theme',
    order: 26,
    displayName: 'Transcript Row Text',
    description: 'Overrides the row text color for the Transcript only. Leave blank to follow the shared list setting.',
    hint: 'Transcript only',
    inputType: 'color',
  },

  themeTranscriptRowDivider: {
    section: 'theme',
    order: 27,
    displayName: 'Transcript Row Divider',
    description: 'Overrides the divider color for the Transcript only. Leave blank to follow the shared list setting.',
    hint: 'Transcript only',
    inputType: 'color',
  },

  themeTabBg: {
    section: 'theme',
    order: 28,
    displayName: 'Unselected Tab Fill',
    description: 'Background color of the content tabs that are not currently selected. Leave blank for the plain labels the player ships with.',
    hint: 'Unselected tab background',
    inputType: 'color',
  },

  themeTabColor: {
    section: 'theme',
    order: 29,
    displayName: 'Unselected Tab Label',
    description: 'Text color of the content tabs that are not currently selected.',
    hint: 'Unselected tab text',
    inputType: 'color',
  },

  themeTabBorder: {
    section: 'theme',
    order: 30,
    displayName: 'Unselected Tab Outline',
    description: 'Outline color of the unselected content tabs. Giving this a color also sets the outline width below, since a color on its own would not be visible.',
    hint: 'Unselected tab border',
    inputType: 'color',
  },

  themeTabBorderWidth: {
    section: 'theme',
    order: 31,
    displayName: 'Tab Outline Width',
    description: 'Thickness of the tab outline. This stays at zero until you choose an outline color, so tabs are never resized by a setting you have not touched.',
    hint: 'Border thickness',
    inputType: 'length',
    cssProperty: 'border-width',
    min: 0,
    max: 4,
    step: 1,
  },

  themeTabRadius: {
    section: 'theme',
    order: 32,
    displayName: 'Tab Corner Rounding',
    description: 'Corner rounding on the UNSELECTED tabs only. The selected tab has its own setting, Active Tab Corners, so the two can differ. Nothing shows here until the unselected tabs have a fill or an outline, since a transparent box has no visible corners.',
    hint: 'Unselected tab corners only',
    inputType: 'length',
    cssProperty: 'border-radius',
    min: 0,
    max: 24,
    step: 1,
  },

  themeTabGap: {
    section: 'theme',
    order: 33,
    displayName: 'Space Between Tabs',
    description: 'Space between the content tabs. Without it, outlined tabs sit flush and read as one segmented control rather than separate buttons.',
    hint: 'Tab spacing',
    inputType: 'length',
    cssProperty: 'gap',
    min: 0,
    max: 32,
    step: 1,
  },

  themeTabActiveBorder: {
    section: 'theme',
    order: 34,
    displayName: 'Selected Tab Outline',
    description: 'Outline color of the selected content tab. Usually left blank: the selected tab already carries a matching invisible outline so it does not shift position when the unselected tabs gain one.',
    hint: 'Selected tab border',
    inputType: 'color',
  },

  themeTabStripBorder: {
    section: 'theme',
    order: 35,
    displayName: 'Lines Above and Below Tabs',
    description: 'Color of the lines above and below the row of tabs. Set it to match your background to remove them. Separate from the list dividers, so removing these lines does not also remove those.',
    hint: 'Separate from the list row dividers',
    inputType: 'color',
  },

  themeTabStripPadding: {
    section: 'theme',
    order: 36,
    displayName: 'Space Around the Tabs',
    description: 'Spacing around the tabs inside their strip, written as CSS padding. Use it to line the tabs up with the text above them.',
    hint: 'Padding inside the tab strip',
    inputType: 'text',
    cssProperty: 'padding',
  },

  themeTabPanelPadding: {
    section: 'theme',
    order: 37,
    displayName: 'Space Around Tab Content',
    description: 'Spacing around the content below the tabs, written as CSS padding. Without it the lists sit flush against the tabs and run wider than the text above them.',
    hint: 'Padding inside the panel below the tabs',
    inputType: 'text',
    cssProperty: 'padding',
  },

  themeTabHoverStrength: {
    section: 'theme',
    order: 38,
    displayName: 'Hover Strength',
    description: 'How strongly a tab changes when the pointer is over it. This is an amount, not a color: the player lightens on dark themes and darkens on light ones, so the same number is correct either way. Zero means no change.',
    hint: 'How much tabs react to hover',
    inputType: 'length',
    cssFormat: 'percentage',
    min: 0,
    max: 40,
    step: 1,
    unit: '%',
  },

  themeFocusRing: {
    section: 'theme',
    order: 39,
    displayName: 'Keyboard Focus Ring',
    description: 'Color of the outline shown when someone moves through the player with a keyboard. It follows your accent color unless set here; give it a distinct color if your accent does not stand out against your background.',
    hint: 'Focus outline',
    inputType: 'color',
  },

  themeCategoryBg: {
    section: 'theme',
    order: 40,
    displayName: 'Category Label Fill',
    description: 'Background color of the category label under the event description. Leave blank for the outlined tag the player ships with.',
    hint: 'Category background',
    inputType: 'color',
  },

  themeCategoryBorder: {
    section: 'theme',
    order: 41,
    displayName: 'Category Label Outline',
    description: 'Outline color of the category label. Set it to match your background to render the category as plain text.',
    hint: 'Category border',
    inputType: 'color',
  },

  themeUnderlayDateColor: {
    section: 'theme',
    order: 42,
    displayName: 'Event Date Color',
    description: 'Color of the event date and time shown with the title.',
    hint: 'Date line',
    inputType: 'color',
  },

  themeHighlightToggleBg: {
    section: 'theme',
    order: 43,
    displayName: 'Highlight Button Fill',
    description: 'Background of the transcript Highlight button while it is switched on. The player ships a fixed yellow that matches the highlight marker itself; set this to make the button follow your brand instead.',
    hint: 'When Highlight is on',
    inputType: 'color',
  },

  themeHighlightToggleBorder: {
    section: 'theme',
    order: 44,
    displayName: 'Highlight Button Outline',
    description: 'Outline of the transcript Highlight button while it is switched on.',
    hint: 'When Highlight is on',
    inputType: 'color',
  },

  themeHighlightToggleColor: {
    section: 'theme',
    order: 45,
    displayName: 'Highlight Button Label',
    description: 'Text color of the transcript Highlight button while it is switched on. Set this whenever you set the fill: the shipped label color assumes a light background and disappears on a dark one.',
    hint: 'When Highlight is on',
    inputType: 'color',
  },

  themeFollowToggleBg: {
    section: 'theme',
    order: 46,
    displayName: 'Follow Button Fill',
    description: 'Background of the transcript Follow button while it is switched on. The player ships a fixed blue that matches the follow marker; set this to make the button follow your brand instead.',
    hint: 'When Follow is on',
    inputType: 'color',
  },

  themeFollowToggleBorder: {
    section: 'theme',
    order: 47,
    displayName: 'Follow Button Outline',
    description: 'Outline of the transcript Follow button while it is switched on.',
    hint: 'When Follow is on',
    inputType: 'color',
  },

  themeFollowToggleColor: {
    section: 'theme',
    order: 48,
    displayName: 'Follow Button Label',
    description: 'Text color of the transcript Follow button while it is switched on. Set this whenever you set the fill: the shipped label color assumes a light background.',
    hint: 'When Follow is on',
    inputType: 'color',
  },


};

// ----- SDK -----

export function getMeta (propertyKey) {
  return metaMap[propertyKey];
}

export function getPropertyKeysBySection (sectionKey) {
  const keys = Object.keys(metaMap).filter(k => metaMap[k].section === sectionKey);
  keys.sort((a, b) => metaMap[a].order - metaMap[b].order);
  return keys;
}

export function isPropertyAvailable (propertyKey, capabilities) {
  const entry = metaMap[propertyKey];
  if (!entry) return false;
  if (!entry.capability) return true;
  return Boolean(capabilities && capabilities[entry.capability]);
}

let _orderedSectionsCache = null;
export function getOrderedSectionsWithProperties () {
  if (_orderedSectionsCache) return _orderedSectionsCache;
  _orderedSectionsCache = sections.map(section => ({
    key: section.key,
    displayName: section.displayName,
    properties: getPropertyKeysBySection(section.key).map(key => ({
      key,
      ...metaMap[key],
    })),
  }));
  return _orderedSectionsCache;
}
